import {
  memo,
  useEffect,
  useState,
  useMemo,
} from 'react';
import moment from 'moment';

import TopicsSvc from '../../../../services/TopicsSvc';
import CompaniesSvc from '../../../../services/dbServices/CompaniesSvc';

import {
  generateTables,
  getSourceName,
  getCountry,
  generateIdentifiers,
} from '../../../../helpers/companyHelpers';

import Preloader from '../../../Preloader';

import withComponentName from '../../../../decorators/withComponentName';

import useSizes from '../../../../hooks/sizes';

import { metaDataGroupInfo, companyDataGroupInfo } from '../../../../data/directory/companyData';
import { DIRECTORIES_PATH } from '../../../../data/directory/constants';

const Info = ({ slug }) => {
  const [metaData, setMetaData] = useState(null);
  const [company, setCompany] = useState(null);
  const [officers, setOfficers] = useState(null);
  const [loading, setLoading] = useState(true);
  const { width } = useSizes();

  const metaDataTables = useMemo(() => metaData && (() => {
    const metaDataSlug = { ...slug };

    const website = company?.website || company?.peers?.[0].company?.website;
    if (website) {
      const url = (website.includes('http') || website.includes('https')) ? website : '//'.concat(website);
      metaDataSlug.website = (
        <a target="_blank" rel="noreferrer noopener" href={url}>
          {website}
        </a>
      );
    }

    const description = company?.description || company?.peers?.[0].company?.description;
    if (description) metaDataSlug.description = description;

    return generateTables(metaDataSlug, metaDataGroupInfo, width);
  })(), [metaData, company, width]);

  const companiesTables = useMemo(() => company && (() => {
    const companyData = { ...company };

    if (officers && officers.length > 0) {
      const CEO = officers[0];

      companyData.officer = {
        id: CEO.officer_id || String(Math.random()),
        name: CEO.url ? (
          <a href={CEO.url} target="_blank" rel="noreferrer noopener">
            {CEO.Officer?.name}
          </a>
        ) : CEO.Officer?.name,
      };
    }
    return generateTables(companyData, companyDataGroupInfo, width);
  })(), [company, officers, width]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const requests = [
        TopicsSvc.getTopicMetaData(slug),
      ];

      const defaultTickers = slug.tickers.filter((ticker) => ticker.default);
      let identifier;
      if (defaultTickers?.length) {
        const identifiers = defaultTickers
          .filter((ticker) => ticker.full_name)
          .map((ticker) => ({
            ticker: ticker.full_name,
          }));
        identifier = generateIdentifiers(identifiers);

        requests.push(
          CompaniesSvc.getCompanyByIdentifier({
            identifier,
          }),
        );
      } else if (slug.legal_entities.length) {
        const identifiers = slug.legal_entities.map((entity) => ({
          companyNumber: entity.legal_entity_id,
        }));
        identifier = generateIdentifiers(identifiers);

        requests.push(
          CompaniesSvc.getCompanyByIdentifier({
            identifier,
          }),
        );
      } else {
        requests.push(Promise.resolve(null));
      }

      const [
        metaDataInfo,
        companyData = null,
      ] = await Promise.all(requests);

      setMetaData(metaDataInfo);

      if (companyData) {
        const companyInfo = {
          ...companyData,
          ...(companyData.incorporation_date && {
            incorporation_date: moment(companyData.incorporation_date).format('D MMM YYYY'),
          }),
          ...(companyData.country && {
            country: {
              id: companyData.country,
              name: getCountry(companyData.country)?.name || companyData.country,
              ...(getCountry(companyData.country) && {
                url: `/directory/filings/${companyData.country}`,
              }),
            },
          }),
          source: {
            id: companyData.source,
            name: getSourceName(companyData.source),
            url: `/directory/filings/${companyData.country}/${companyData.source}`,
          },
        };

        setCompany(companyInfo);

        const companyNumber = (
          companyData.companyNumber
          || companyData?.peers?.[0]?.company?.companyNumber
        );

        if (companyInfo.country && companyNumber) {
          CompaniesSvc
            .getCompanyByIdentifier({ identifier: `NO:${companyNumber}` })
            .then((companyInfoDetailed) => {
              if (companyInfoDetailed) {
                setCompany({
                  ...companyInfoDetailed,
                  ...companyInfo,
                  natureOfBusiness: companyInfoDetailed.natureOfBusiness,
                });
              }
            });

          let identifierOfficer;
          if (companyInfo.companyDomainName) identifierOfficer = `CD:${companyInfo.companyDomainName}`;
          if (!identifierOfficer && companyNumber) identifierOfficer = `NO:${companyNumber}`;

          if (identifierOfficer) {
            CompaniesSvc
              .getCompanyOfficers({ identifier: identifierOfficer })
              .then((companyOfficers) => {
                if (companyOfficers) {
                  setOfficers(companyOfficers.filter((officer) => officer.occupation !== 'Investor'));

                  Promise.all(
                    companyOfficers.map((officer) => (
                      officer?.Officer?.slug_name
                      && officer.occupation !== 'Investor'
                      && TopicsSvc.getTopicBySlug(officer.Officer.slug_name)
                    )),
                  ).then((topics) => {
                    topics.forEach((topic) => {
                      setOfficers((currentOfficers) => (
                        currentOfficers.map((officer) => ({
                          ...officer,
                          ...(Boolean(topic) && {
                            url: `${DIRECTORIES_PATH}/${topic.asset_class.slug}/${topic.slug}`,
                          }),
                        }))
                      ));
                    });
                  });
                }
              });
          }
        }
      } else {
        setCompany(null);
        setOfficers(null);
      }
      setLoading(false);
    })();
  }, [slug]);

  return (
    loading ? <Preloader loading static /> : (
      <div className="info-container">
        {/* Company data tables */}
        {companiesTables}
        {/* Meta data tables */}
        {metaDataTables}
      </div>
    )
  );
};

export default withComponentName(memo(Info));
